
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Calendar, 
  Stethoscope, 
  MessageSquare, 
  ShoppingCart,
  Activity,
  TrendingUp,
  Clock,
  CalendarDays
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

interface DashboardHomeSectionProps {
  stats?: {
    appointments: number;
    services: number;
    messages: number;
    purchaseRequests: number;
    products: number;
  };
  onNavigate: (section: string) => void;
  onNewAppointment: () => void;
  onNewService: () => void;
}

export const DashboardHomeSection = ({ stats, onNavigate, onNewAppointment, onNewService }: DashboardHomeSectionProps) => {
  const chartData = [
    { month: "Jan", appointments: 65, services: 28 },
    { month: "Fév", appointments: 59, services: 48 },
    { month: "Mar", appointments: 80, services: 40 },
    { month: "Avr", appointments: 81, services: 19 },
    { month: "Mai", appointments: 56, services: 86 },
    { month: "Juin", appointments: 55, services: 27 },
  ];

  const pieData = [
    { name: "Rendez-vous", value: stats?.appointments || 0, color: "#3B82F6" },
    { name: "Services", value: stats?.services || 0, color: "#10B981" },
    { name: "Messages", value: stats?.messages || 0, color: "#8B5CF6" },
    { name: "Demandes", value: stats?.purchaseRequests || 0, color: "#F59E0B" },
  ];

  const statItems = [
    { 
      title: "Rendez-vous", 
      value: stats?.appointments || 0, 
      icon: Calendar,
      color: "text-blue-600",
      bgColor: "bg-blue-50"
    },
    { 
      title: "Services", 
      value: stats?.services || 0, 
      icon: Stethoscope,
      color: "text-green-600",
      bgColor: "bg-green-50"
    },
    { 
      title: "Messages", 
      value: stats?.messages || 0, 
      icon: MessageSquare,
      color: "text-purple-600",
      bgColor: "bg-purple-50"
    },
    { 
      title: "Demandes d'achat", 
      value: stats?.purchaseRequests || 0, 
      icon: ShoppingCart,
      color: "text-orange-600",
      bgColor: "bg-orange-50"
    },
  ];

  return (
    <div className="space-y-6 p-4">
      <div>
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p className="text-gray-600">Vue d'ensemble de votre activité professionnelle</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-4">
        {statItems.map((item) => {
          const Icon = item.icon;
          return (
            <Card key={item.title} className={`${item.bgColor} border-none shadow-sm`}>
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-600">
                  {item.title}
                </CardTitle>
                <Icon className={`h-4 w-4 ${item.color}`} />
              </CardHeader>
              <CardContent>
                <p className={`text-2xl font-bold ${item.color}`}>{item.value}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Analytics Charts */}
      <div className="grid grid-cols-1 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Activité Mensuelle
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <XAxis dataKey="month" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip />
                  <Line 
                    type="monotone" 
                    dataKey="appointments" 
                    stroke="#3B82F6" 
                    strokeWidth={2} 
                    name="Rendez-vous" 
                  />
                  <Line 
                    type="monotone" 
                    dataKey="services" 
                    stroke="#10B981" 
                    strokeWidth={2} 
                    name="Services" 
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Répartition des Activités
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-4">
              {pieData.map((entry) => (
                <div key={entry.name} className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-sm text-gray-600">{entry.name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Actions Rapides</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            <Button variant="outline" className="flex items-center gap-2" onClick={onNewAppointment}>
              <Calendar className="h-4 w-4" />
              Nouveau RDV
            </Button>
            <Button variant="outline" className="flex items-center gap-2" onClick={onNewService}>
              <Stethoscope className="h-4 w-4" />
              Nouveau Service
            </Button>
            <Button variant="outline" className="flex items-center gap-2" onClick={() => onNavigate('Rendez-vous')}>
              <CalendarDays className="h-4 w-4" />
              Demandes de RDV
            </Button>
            <Button variant="outline" className="flex items-center gap-2" onClick={() => onNavigate('Demandes d\'achat')}>
              <ShoppingCart className="h-4 w-4" />
              Demandes d'achat
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
